"use client"

import styled from "@emotion/styled"
import { useEffect, useRef, useState } from "react"
import { extractProperty, getLinkHrefFromMultiUrlPickerItem } from "utils"
import { LANDING_ELEMENTS, useLandingAnalytics } from "../analytics"
import { BRUSH_UNDERLINE_URI } from "../brushUnderline"
import { handleAnchorScroll } from "../scrollToAnchor"

type Props = {
  data: any
}

// --- Viewport entrance hook (fires once per page load) ---

function useInViewOnce<T extends HTMLElement>() {
  const ref = useRef<T>(null)
  const [inView, setInView] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el || inView) return
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setInView(true)
      return
    }
    const o = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setInView(true)
          o.disconnect()
        }
      },
      { threshold: 0, rootMargin: "0px 0px -15% 0px" },
    )
    o.observe(el)
    return () => o.disconnect()
  }, [inView])
  return { ref, inView }
}

// --- Styled Components ---

const Section = styled.section`
  padding: 64px 20px;
  background-color: var(--comp-bg);
  color: var(--comp-text);
  overflow: hidden;

  ${(props) => props.theme.breakpoints.up("md")} {
    /* horizontal padding scales down below the 1440 design width so the two
       fixed columns stop being cut off between 1280–1440px (ASH-1990) */
    padding: 120px clamp(24px, 8.33vw, 120px);
  }
`

/* mobile: stacked (title, body, buttons). desktop: title column on the RTL start
   (right) side, body + buttons on the other side. Text is start-aligned on both. */
const Inner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  max-width: 1200px;
  margin: 0 auto;
  text-align: start;

  ${(props) => props.theme.breakpoints.up("md")} {
    flex-direction: row;
    justify-content: flex-end;
    align-items: flex-start;
    gap: 80px;
  }
`

/* first in JSX = right side in RTL. Columns hold the 500/620 design widths as a
   flex-basis but may shrink (flex-shrink 1 + min-width 0) so they never overflow
   the section between 1280–1440px (ASH-1990); max-width keeps them from growing
   past the design size on wide screens. */
const TitleColumn = styled.div`
  ${(props) => props.theme.breakpoints.up("md")} {
    flex: 1 1 500px;
    max-width: 500px;
    min-width: 0;
  }
`

const TextColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;

  ${(props) => props.theme.breakpoints.up("md")} {
    flex: 1 1 620px;
    max-width: 620px;
    min-width: 0;
    gap: 32px;
  }
`

/* Rich-text title: the emphasized word carries the Figma brush underline.
   Desktop keeps the non-emphasized part lighter (500); mobile is all 700. */
const Title = styled.h2`
  font-size: 40px;
  font-weight: 700;
  line-height: 1.3;
  color: var(--comp-heading);
  margin: 0;

  p {
    margin: 0;
    display: inline;
  }
  strong {
    position: relative;
    display: inline-block;
    font-weight: inherit;
    text-decoration: none;
  }
  strong::after {
    content: "";
    position: absolute;
    inset-inline: 0;
    bottom: -4px;
    height: 6px;
    background-color: currentColor;
    -webkit-mask: url("${BRUSH_UNDERLINE_URI}") no-repeat center / 100% 100%;
    mask: url("${BRUSH_UNDERLINE_URI}") no-repeat center / 100% 100%;
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 60px;
    font-weight: 500;

    strong {
      font-weight: 700;
    }
  }
`

const BodyCopy = styled.p`
  font-size: 18px;
  font-weight: 700;
  line-height: 1.5;
  color: var(--comp-text);
  margin: 0;
  /* keep the line breaks the editor typed in the textarea */
  white-space: pre-line;

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 20px;
    font-weight: 400;
  }
`

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;

  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 24px;
  }
`

/* primary = filled (heading colour), secondary = outline. Both use theme vars so
   they stay readable in every colour package. */
const CtaButton = styled.a<{ $primary?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  padding: 0 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
  text-decoration: none;
  white-space: nowrap;
  border: 1px solid var(--comp-heading);
  background-color: ${({ $primary }) => ($primary ? "var(--comp-heading)" : "transparent")};
  color: ${({ $primary }) => ($primary ? "var(--comp-bg)" : "var(--comp-heading)")};
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.9;
  }

  &:focus-visible {
    outline: 2px dashed var(--comp-heading);
    outline-offset: 2px;
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    height: 48px;
    font-size: 20px;
  }
`

/* Entrance per the Figma prototype (ASH-1956): the title is uncovered by a wipe
   that runs from the reading start (right in RTL) to the left, the description
   fades in, and the buttons are shown immediately (no reveal).

   The wipe is a clip-path inset driven by INLINE style on the element so the
   emotion RTL plugin can't flip the inset direction. Only the left inset
   animates; the top/bottom insets are held negative so the brush underline
   (which paints below the text box) is never clipped vertically. */
const TitleReveal = styled.div`
  transition: clip-path 0.85s cubic-bezier(0.22, 1, 0.36, 1);
  will-change: clip-path;
`

const FadeReveal = styled.div<{ $in: boolean; $delay: number }>`
  opacity: ${({ $in }) => ($in ? 1 : 0)};
  transition: opacity 0.7s ease;
  transition-delay: ${({ $delay }) => $delay}ms;
`

// --- Parsing ---

type LinkItem = { name: string; url: string; target?: string } | null

function parseLink(raw: any): LinkItem {
  const link = Array.isArray(raw) ? raw[0] : undefined
  if (!link?.name) return null
  return {
    name: link.name,
    url: getLinkHrefFromMultiUrlPickerItem(link) || "#",
    target: link.target,
  }
}

// --- Main Component ---

function AboutOpening2({ data }: Props) {
  const analytics = useLandingAnalytics()
  const { ref, inView } = useInViewOnce<HTMLDivElement>()

  const title = extractProperty(data, "title") as string | ""
  const bodyCopy = extractProperty(data, "bodyCopy") as string | ""
  const primary = parseLink(extractProperty(data, "primaryButton"))
  const secondary = parseLink(extractProperty(data, "secondaryButton"))

  if (!title && !bodyCopy && !primary && !secondary) return null

  const linkProps = (l: NonNullable<LinkItem>) =>
    l.target === "_blank" ? { target: "_blank", rel: "noopener noreferrer" } : {}

  return (
    <Section>
      <Inner ref={ref}>
        {title && (
          <TitleColumn>
            {/* inline clip-path bypasses the RTL plugin: the left inset animates
                100% -> 0 to uncover the text from the right (reading start)
                leftward, while top/bottom stay negative so the underline below
                the text box is never clipped */}
            <TitleReveal
              style={{
                clipPath: inView ? "inset(-40px 0 -40px 0)" : "inset(-40px 0 -40px 100%)",
              }}
            >
              <Title dangerouslySetInnerHTML={{ __html: title }} />
            </TitleReveal>
          </TitleColumn>
        )}
        {(bodyCopy || primary || secondary) && (
          <TextColumn>
            {bodyCopy && (
              <FadeReveal $in={inView} $delay={150}>
                <BodyCopy>{bodyCopy}</BodyCopy>
              </FadeReveal>
            )}
            {(primary || secondary) && (
              <Actions>
                {/* primary first in JSX = right side in RTL, per Figma */}
                {primary && (
                  <CtaButton
                    $primary
                    href={primary.url}
                    onClick={(e) => {
                      analytics.ctaClick(primary.name, LANDING_ELEMENTS.aboutOpening)
                      handleAnchorScroll(e, primary.url)
                    }}
                    {...linkProps(primary)}
                  >
                    {primary.name}
                  </CtaButton>
                )}
                {secondary && (
                  <CtaButton
                    href={secondary.url}
                    onClick={(e) => {
                      analytics.ctaClick(secondary.name, LANDING_ELEMENTS.aboutOpening)
                      handleAnchorScroll(e, secondary.url)
                    }}
                    {...linkProps(secondary)}
                  >
                    {secondary.name}
                  </CtaButton>
                )}
              </Actions>
            )}
          </TextColumn>
        )}
      </Inner>
    </Section>
  )
}

export default AboutOpening2
